import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
import { addressSnapshot } from './orders.js';
import { packageDto } from './packaging.js';
/** docs/design/01-operations-data-model.md section 8: a picking batch groups a gift shop's orders. */
export const batchKind = z.enum(['single_shipment', 'multi_shipment']);
export const batchStatus = z.enum(['created', 'printed', 'picking', 'picked', 'packed', 'staged', 'shipped', 'cancelled']);
export const batchDto = z.object({
    id: uuid,
    number: z.string(),
    shopId: uuid,
    shopName: z.string(),
    warehouseId: uuid,
    warehouseCode: z.string(),
    kind: batchKind,
    deliveryAddress: addressSnapshot.nullable(),
    status: batchStatus,
    orderCount: z.number().int(),
    unitCount: z.number().int(),
    packedBy: z.string().nullable(),
    packedAt: isoDateTime.nullable(),
    stagingLocationId: uuid.nullable(),
    stagingLocationCode: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    shippedAt: isoDateTime.nullable(),
    printedAt: isoDateTime.nullable(),
    documents: z.object({ overview: uuid.nullable(), pickList: uuid.nullable(), totals: uuid.nullable(), giftList: uuid.nullable() }),
    createdBy: z.string(),
    createdAt: isoDateTime,
});
export const batchOrderDto = z.object({
    fulfilmentId: uuid,
    fulfilmentNumber: z.string(),
    fulfilmentStatus: z.string(),
    orderId: uuid,
    orderNumber: z.number().int(),
    customerName: z.string().nullable(),
    department: z.string().nullable(),
    lines: z.array(z.object({ sku: z.string(), title: z.string(), variantTitle: z.string().nullable(), quantity: z.number().int(), quantityPicked: z.number().int(), locationCode: z.string().nullable() })),
});
export const batchDetailDto = batchDto.extend({
    packages: z.array(packageDto),
    orders: z.array(batchOrderDto),
    /** Per SKU across the batch, in walking order. */
    pickList: z.array(z.object({ sku: z.string(), title: z.string(), variantTitle: z.string().nullable(), quantity: z.number().int(), locationCode: z.string().nullable(), locationBarcode: z.string().nullable(), onHand: z.number().int(), barcodes: z.array(z.string()), imageUrl: z.string().nullable() })),
});
/** What a batch of up to `limit` orders of the shop would contain, before creating it. */
export const batchPreviewRequest = z.object({
    shopId: uuid,
    warehouseId: uuid.optional(),
    kind: batchKind.default('single_shipment'),
    limit: z.number().int().min(1).max(2000).default(200),
    department: z.string().trim().max(120).optional(),
    /** Only orders whose every line is available; otherwise partially available orders are left out too. */
    orderIds: z.array(uuid).max(2000).optional(),
});
export const batchPreviewDto = z.object({
    warehouseId: uuid,
    warehouseCode: z.string(),
    packable: z.number().int(),
    unpackable: z.number().int(),
    units: z.number().int(),
    short: z.array(z.object({ sku: z.string(), title: z.string(), needed: z.number().int(), available: z.number().int() })),
    departments: z.array(z.object({ department: z.string(), orders: z.number().int() })),
});
export const createBatchRequest = batchPreviewRequest.extend({
    printerId: uuid.optional(),
});
export const batchQuery = z.object({
    shopId: uuid.optional(),
    warehouseId: uuid.optional(),
    status: batchStatus.optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const stageBatchRequest = z.object({ stagingLocationId: uuid.nullable() });
export const shipBatchRequest = z.object({ trackingNumber: z.string().trim().max(100).nullable().optional() });
export const printBatchRequest = z.object({ printerId: uuid, documents: z.array(z.enum(['overview', 'pickList', 'totals', 'giftList'])).min(1).default(['overview', 'pickList', 'totals', 'giftList']) });
//# sourceMappingURL=batches.js.map